import React, { useMemo, useState } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { WordItem } from './WordItem';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { speak, isSpeechSupported } from '../../utils/speak';

export const WordList = ({
  groupedWords,
  groupBy,
  onChangeGrouping,
  isLoading,
  error,
  hasMore,
  onLoadMore,
  onEdit,
  onDelete,
  onUpdateLearningLevel,
  onRetry,
  onWordClick,
  // Search props
  searchQuery = '',
  isSearching = false,
  onSearch,
  onClearSearch,
}) => {
  const [learningLevelFilter, setLearningLevelFilter] = useLocalStorage('wordList.filterV2', []);
  const [viewMode, setViewMode] = useLocalStorage('wordList.viewMode', 'cards');
  const [revealedWords, setRevealedWords] = useState({});
  const loadMoreRef = useInfiniteScroll(onLoadMore, hasMore, isLoading);

  const toggleRevealed = (wordId) =>
    setRevealedWords(prev => ({ ...prev, [wordId]: !prev[wordId] }));

  const groupOptions = [
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
  ];

  const filterOptions = [
    { value: 'learning', label: 'Learning' },
    { value: 'hard', label: 'Hard' },
    { value: 'mastered', label: 'Mastered' },
  ];

  const toggleFilter = (value) => {
    setLearningLevelFilter(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
    });
  };

  const levelConfig = {
    mastered: {
      label: 'Mastered',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      dotColor: 'bg-green-500',
    },
    learning: {
      label: 'Learning',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      dotColor: 'bg-blue-500',
    },
    hard: {
      label: 'Hard',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-800',
      dotColor: 'bg-orange-500',
    },
  };

  // Filter grouped words by learning level
  const filteredGroupedWords = useMemo(() => {
    const activeFilters = Array.isArray(learningLevelFilter) ? learningLevelFilter : [];
    if (activeFilters.length === 0) {
      return groupedWords;
    }

    const filtered = {};
    Object.entries(groupedWords).forEach(([date, words]) => {
      const filteredWords = words.filter(
        (word) => activeFilters.includes(word.learningLevel || 'learning')
      );
      if (filteredWords.length > 0) {
        filtered[date] = filteredWords;
      }
    });
    return filtered;
  }, [groupedWords, learningLevelFilter]);

  const isEmpty = Object.keys(filteredGroupedWords).length === 0;
  const noWordsAtAll = Object.keys(groupedWords).length === 0;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Search and Filter controls */}
      <div className="mb-6 space-y-3">
        {/* Search input */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isSearching ? (
              <LoadingSpinner size="small" />
            ) : (
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            )}
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearch && onSearch(e.target.value)}
            placeholder="Search words..."
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
          {searchQuery && (
            <button
              onClick={onClearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Filter and Group controls */}
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          {/* Learning Level Filter */}
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            <span className="text-sm text-gray-600">Filter:</span>
            <div className="flex gap-1 flex-wrap">
              {filterOptions.map((option) => {
                const active = Array.isArray(learningLevelFilter) && learningLevelFilter.includes(option.value);
                const cfg = levelConfig[option.value];
                return (
                  <button
                    key={option.value}
                    onClick={() => toggleFilter(option.value)}
                    className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border transition-colors ${
                      active
                        ? `${cfg.bgColor} ${cfg.textColor} border-transparent`
                        : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {active && <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotColor}`} />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date Grouping */}
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="text-sm text-gray-600">Group:</span>
            <select
              value={groupBy}
              onChange={(e) => onChangeGrouping(e.target.value)}
              className="px-2 sm:px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {groupOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 sm:gap-2">
            <span className="text-sm text-gray-600">View:</span>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-1.5 ${viewMode === 'cards' ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                title="Cards view"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 border-l border-gray-300 ${viewMode === 'table' ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                title="Table view"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 6h18M3 18h18M9 6v12M15 6v12" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('reverse')}
                className={`p-1.5 border-l border-gray-300 ${viewMode === 'reverse' ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                title="Reverse view"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 7l4-4M4 7l4 4M20 17H4M20 17l-4-4M20 17l-4 4" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && noWordsAtAll ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to fetch words</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <button
            onClick={onRetry}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Try Again
          </button>
        </div>
      ) : noWordsAtAll && isLoading ? (
        <div className="text-center py-12">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-sm text-gray-600">Fetching words...</p>
        </div>
      ) : noWordsAtAll && searchQuery ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No words match your search</h3>
          <p className="mt-1 text-sm text-gray-500">
            Try a different search term or{' '}
            <button
              onClick={onClearSearch}
              className="text-primary-600 hover:text-primary-700 underline"
            >
              clear the search
            </button>
          </p>
        </div>
      ) : noWordsAtAll ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No words yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding your first word!
          </p>
        </div>
      ) : isEmpty ? (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No words match this filter</h3>
          <p className="mt-1 text-sm text-gray-500">
            Try changing the learning level filter
          </p>
        </div>
      ) : viewMode === 'reverse' ? (
        /* Reverse View */
        <div className="space-y-6">
          {Object.entries(filteredGroupedWords).map(([date, words]) => (
            <div key={date}>
              <h3 className="text-lg font-semibold text-gray-700 mb-3">{date}</h3>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full table-fixed divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-2/5 px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Translation</th>
                      <th className="w-1/4 sm:w-[25%] px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Word</th>
                      <th className="w-1/5 px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                      <th className="hidden sm:table-cell sm:w-[15%] px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {words.filter(word => word.translation?.text).map((word) => {
                      const level = word.learningLevel || 'learning';
                      const cfg = levelConfig[level];
                      const isRevealed = !!revealedWords[word.wordId];
                      return (
                        <tr key={word.wordId} className="hover:bg-gray-50">
                          <td className="px-3 sm:px-4 py-3">
                            <span className="text-sm text-gray-700 italic block truncate" title={word.translation?.text || ''}>
                              {word.translation?.text || '—'}
                            </span>
                          </td>
                          <td className="px-3 sm:px-4 py-3">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span
                                className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-primary-600 transition-colors"
                                onClick={() => onWordClick && onWordClick(word)}
                              >{word.word}</span>
                              {isSpeechSupported() && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); speak(word.word); }}
                                  className="flex-shrink-0 text-gray-400 hover:text-primary-600 transition-colors"
                                  title="Pronounce"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6a7 7 0 010 12M8 9l3-3v12l-3-3H5a1 1 0 01-1-1v-4a1 1 0 011-1h3z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bgColor} ${cfg.textColor}`}>
                              <span className={`w-1.5 h-1.5 mr-1 sm:mr-1.5 rounded-full ${cfg.dotColor}`}></span>
                              <span className="hidden sm:inline">{cfg.label}</span>
                              <span className="sm:hidden">{cfg.label.charAt(0)}</span>
                            </span>
                          </td>
                          <td className="hidden sm:table-cell px-3 sm:px-4 py-3 whitespace-nowrap text-right">
                            <button
                              onClick={() => onEdit(word)}
                              className="p-1 sm:p-1.5 text-primary-600 hover:bg-primary-50 rounded transition-colors mr-0.5 sm:mr-1"
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => onDelete(word.wordId)}
                              className="p-1 sm:p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : viewMode === 'cards' ? (
        <div className="space-y-8">
          {Object.entries(filteredGroupedWords).map(([date, words]) => (
            <div key={date}>
              <h3 className="text-lg font-semibold text-gray-700 mb-3">{date}</h3>
              <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
                {words.map((word) => (
                  <WordItem
                    key={word.wordId}
                    word={word}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onUpdateLearningLevel={onUpdateLearningLevel}
                    onWordClick={onWordClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Table View */
        <div className="space-y-6">
          {Object.entries(filteredGroupedWords).map(([date, words]) => (
            <div key={date}>
              <h3 className="text-lg font-semibold text-gray-700 mb-3">{date}</h3>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full table-fixed divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-2/5 sm:w-1/5 px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Word</th>
                      <th className="hidden sm:table-cell sm:w-2/5 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Translation</th>
                      <th className="w-1/4 sm:w-1/5 px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                      <th className="w-[35%] sm:w-1/5 px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {words.map((word) => {
                      const level = word.learningLevel || 'learning';
                      const config = levelConfig[level];
                      return (
                        <tr key={word.wordId} className="hover:bg-gray-50">
                          <td className="px-3 sm:px-4 py-3">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span
                                className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-primary-600 transition-colors"
                                title={word.word}
                                onClick={() => onWordClick && onWordClick(word)}
                              >
                                {word.word}
                              </span>
                              {isSpeechSupported() && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); speak(word.word); }}
                                  className="flex-shrink-0 text-gray-400 hover:text-primary-600 transition-colors"
                                  title="Pronounce"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6a7 7 0 010 12M8 9l3-3v12l-3-3H5a1 1 0 01-1-1v-4a1 1 0 011-1h3z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="hidden sm:table-cell px-4 py-3">
                            <span className="text-sm text-gray-500 italic block truncate" title={word.translation?.text || ''}>
                              {word.translation?.text || '—'}
                            </span>
                          </td>
                          <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
                              <span className={`w-1.5 h-1.5 mr-1 sm:mr-1.5 rounded-full ${config.dotColor}`}></span>
                              <span className="hidden sm:inline">{config.label}</span>
                              <span className="sm:hidden">{config.label.charAt(0)}</span>
                            </span>
                          </td>
                          <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-right">
                            <button
                              onClick={() => onEdit(word)}
                              className="p-1 sm:p-1.5 text-primary-600 hover:bg-primary-50 rounded transition-colors mr-0.5 sm:mr-1"
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => onDelete(word.wordId)}
                              className="p-1 sm:p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Infinite scroll trigger - only show for pagination loading */}
      {!isEmpty && (
        <div ref={loadMoreRef} className="py-8">
          {isLoading && (
            <div className="text-center">
              <LoadingSpinner />
              <p className="mt-2 text-sm text-gray-600">Loading more words...</p>
            </div>
          )}
        </div>
      )}

      {!hasMore && !isEmpty && (
        <p className="text-center text-gray-500 py-4">
          You've reached the end of your vocabulary list
        </p>
      )}
    </div>
  );
};
